// src/components/booking/CalendarGrid.tsx
// Calendario mensual. Extraído de BookingModal.tsx SIN cambios de lógica ni de UI.
'use client';

import { Button, Spinner, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslation } from '@/i18n/hooks/useTranslation';
import { startOfMonth, endOfMonth, toYMD, rgba, type RGB } from './bookingShared';

/** Iniciales de la semana (domingo primero): claves de booking.calendar. */
const WEEKDAY_KEYS = ['wd0', 'wd1', 'wd2', 'wd3', 'wd4', 'wd5', 'wd6'] as const;

/* ========================= Calendario ========================= */
export function CalendarGrid({
  value, onChange, monthDate, setMonthDate, availableDays, loading, brandRGB, locale,
}: {
  value?: string | null; onChange: (ymd: string) => void;
  monthDate: Date; setMonthDate: (d: Date) => void;
  availableDays: Record<string, boolean>; loading: boolean;
  brandRGB: RGB | null;
  /**
   * Locale del nombre del mes. Sin él (`undefined`) es el del navegador, como
   * siempre; Brasil pasa 'pt-BR' y EE.UU. 'en-US' (`bookingDateLocale`).
   */
  locale?: string;
}) {
  const { t } = useTranslation();
  const monthName = monthDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const first = startOfMonth(monthDate);
  const last = endOfMonth(monthDate);
  const firstWeekday = (first.getDay() + 7) % 7;
  const totalDays = last.getDate();

  const prevMonth = () => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1));
  const nextMonth = () => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1));

  const cells: Array<{ label: string; ymd?: string; disabled?: boolean }> = [];
  for (let i = 0; i < firstWeekday; i++) cells.push({ label: '' });
  for (let d = 1; d <= totalDays; d++) {
    const ymd = toYMD(new Date(monthDate.getFullYear(), monthDate.getMonth(), d));
    const enabled = availableDays[ymd] === true;
    cells.push({ label: String(d), ymd, disabled: !enabled });
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-800/50 p-2 sm:p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="text-sm sm:text-base font-semibold capitalize text-gray-900 dark:text-white">{monthName}</div>
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="light" onPress={prevMonth} aria-label={t('booking.calendar.prevMonth')}><Icon icon="mdi:chevron-left" /></Button>
          <Button size="sm" variant="light" onPress={nextMonth} aria-label={t('booking.calendar.nextMonth')}><Icon icon="mdi:chevron-right" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-[11px] sm:text-xs text-gray-500 dark:text-gray-400 mb-1">
        {WEEKDAY_KEYS.map((k, i) => <div key={i} className="text-center py-0.5">{t(`booking.calendar.${k}`)}</div>)}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, idx) => {
          if (!c.ymd) return <div key={`e-${idx}`} className="h-8 sm:h-9" />;
          const selected = value === c.ymd;
          const disabled = !!c.disabled;

          const baseClass = "h-8 sm:h-9 rounded-lg border text-sm grid place-items-center transition-colors";
          const disabledClass = "border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed";
          const normalClass = "border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50";

          const inner = disabled ? (
            <div className={`${baseClass} ${disabledClass}`} title={c.ymd}>{c.label}</div>
          ) : selected ? (
            <div
              className={`${baseClass} font-semibold`}
              title={c.ymd}
              style={{ borderColor: rgba(brandRGB, 0.7), boxShadow: `0 0 0 2px ${rgba(brandRGB, 0.2)}`, backgroundColor: rgba(brandRGB, 0.15) }}
            >
              {c.label}
            </div>
          ) : (
            <div className={`${baseClass} ${normalClass}`} title={c.ymd}>{c.label}</div>
          );

          if (disabled) {
            return (
              <div key={`d-${c.ymd}`} aria-disabled className="relative">
                {inner}
                <Tooltip content={t('booking.calendar.noSlots')} placement="top" delay={180}><span className="absolute inset-0" /></Tooltip>
              </div>
            );
          }
          return <button key={`d-${c.ymd}`} onClick={() => onChange(c.ymd!)} aria-pressed={selected}>{inner}</button>;
        })}
      </div>

      {loading && (
        <div className="flex items-center gap-2 mt-3 text-xs text-gray-500 dark:text-gray-400">
          <Spinner size="sm" />{` ${t('booking.calendar.loadingMonth')}`}
        </div>
      )}
    </div>
  );
}

export default CalendarGrid;
